import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthCheck } from '../../hooks/useAuthCheck';
import { usePolling } from '../../hooks/usePolling';
import { handleUnifiedError } from '../../utils/unifiedErrorHandler';
import { useI18n } from '../../contexts/I18nContext';
import imageService from '../../services/imageService';
import LoginModal from '../Login/LoginModal';
import toast from 'react-hot-toast';
import GenerateButtonWithCredits from './parts/GenerateButtonWithCredits';
import ComponentRenderer from './parts/ComponentRenderer';
import ResultSection from './parts/ResultSection';
import useGenerate, { normalizeGenerationResult } from './parts/useGenerate';

import { COMPONENT_TYPES } from './config/componentConfig';
import { buildPrompt } from './utils/promptBuilder';
import { generateDownloadFilename } from '../../utils/fileUtils';
import { API_BASE_URL } from '../../lib/constants';

import './AiToolTemplate.scss';

const AiToolTemplateV2 = ({ 
  toolName, 
  toolIcon, 
  toolDescription, 
  config, // 新的配置对象
  gradientColors = ['#4CAF50', '#00e5b8'],
  toolId // 新增toolId属性，用于标识AI工具类型
}) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  
  // 使用统一的认证检查Hook
  const { checkAuthAndExecute, showLoginModal, handleCloseLogin, handleLoginSuccess } = useAuthCheck();
  
  // 组件状态管理
  const [componentValues, setComponentValues] = useState({});
  const [componentValidations, setComponentValidations] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [error, setError] = useState('');

  // 初始化轮询Hook
  const { startPolling, stopPolling } = usePolling({
    maxAttempts: 200,
    pollInterval: 3000,
    initialDelay: 1000,
    onSuccess: (result) => {
      const prompt = buildPrompt(promptConfig, componentValues);
      const value = normalizeGenerationResult(result, prompt, {
        useAbsoluteUrl: false,
        baseUrl: API_BASE_URL
      });
      setGenerationProgress(100);
      if (value) {
        setGeneratedImage(value);
      }
      toast.success(t('aiToolTemplate.generationSuccess'));
      setIsGenerating(false);
    },
    onFailed: (result) => {
      const errorMessage = result?.message || t('aiToolTemplate.generationFailed');
      setError(errorMessage);
      // 不要重复显示toast，因为usePolling已经显示了
      setIsGenerating(false);
      setGenerationProgress(0);
    },
    onError: () => {
      setIsGenerating(false);
      setGenerationProgress(0);
    },
    onTimeout: () => {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  });

  // 超时保护机制：防止进度条无限卡住
  useEffect(() => {
    let timeoutId;
    if (isGenerating) {
      timeoutId = setTimeout(() => {
        console.warn('AI tool generation timeout after 5 minutes, forcing reset');
        setIsGenerating(false);
        setGenerationProgress(0);
        stopPolling();
        handleUnifiedError(new Error(t('aiToolTemplate.generationTimeout')), t, {
          showToast: true,
          severity: 'warning'
        });
      }, 600000);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isGenerating, stopPolling, t]);

  const { components = [], prompt: promptConfigFromConfig, additionalParams } = config || {};
  const promptConfig = promptConfigFromConfig;

  // 页面加载时重置滚动位置
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // 组件卸载时停止轮询
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // 初始化组件值
  useEffect(() => {
    const initialValues = {};
    components.forEach(component => {
      if (component.defaultValue !== undefined) {
        initialValues[component.id] = component.defaultValue;
      }
    });
    setComponentValues(initialValues);
  }, [components]);

  // 处理组件值变化
  const handleComponentValueChange = (componentId, value) => {
    setComponentValues(prev => ({
      ...prev,
      [componentId]: value
    }));
  };

  // 处理组件验证状态变化
  const handleComponentValidationChange = (componentId, isValid, message) => {
    setComponentValidations(prev => ({
      ...prev,
      [componentId]: { isValid, message }
    }));
  };

  const { handleGenerate, validateAllComponents } = useGenerate({
    components,
    promptConfig,
    additionalParams,
    toolId,
    componentValues,
    componentValidations,
    setIsGenerating,
    setGenerationProgress,
    setGeneratedImage,
    setError,
    startPolling,
    stopPolling,
    checkAuthAndExecute,
    t
  });

  // 处理下载
  const handleDownload = async () => {
    if (!generatedImage?.url) return;

    try {
      toast.success(t('aiToolTemplate.downloadStarted'));
      const filename = generateDownloadFilename(generatedImage.id || toolId);
      await imageService.downloadImage(generatedImage.url, `${filename}.png`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(t('aiToolTemplate.downloadFailed'));
    }
  };

  const visibleComponents = (components || []).filter(
    component => component && component.visible !== false
  );

  const componentsWithIndex = visibleComponents.map((component, index) => ({
    component,
    index
  }));

  const sortedComponents = componentsWithIndex
    .slice()
    .sort((a, b) => {
      const getBaseOrder = (item) => {
        const { component, index } = item;
        if (typeof component.order === 'number') {
          return component.order;
        }
        return index + 1;
      };

      const getPriorityOffset = (item) => {
        const { component } = item;
        const hasExplicitOrder = typeof component.order === 'number';
        if (!hasExplicitOrder) {
          if (component.id === 'supplementaryPrompt') {
            return 1000;
          }
          if (component.type === COMPONENT_TYPES.SIZE_SELECTOR) {
            return -1000;
          }
        }
        return 0;
      };

      const aOrder = getBaseOrder(a) + getPriorityOffset(a);
      const bOrder = getBaseOrder(b) + getPriorityOffset(b);

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return a.index - b.index;
    })
    .map(item => item.component);

  const sizeSelectorId = components.find(c => c?.type === COMPONENT_TYPES.SIZE_SELECTOR)?.id;
  const generationSettings = sizeSelectorId ? componentValues[sizeSelectorId] : undefined;
  
  return (
    <div className="ai-tool-template" style={{ '--gradient-start': gradientColors[0], '--gradient-end': gradientColors[1] }}>
      <div className="page-header">
        <button className="back-button" onClick={() => navigate('/ai-tools-workbench')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('aiToolTemplate.backToWorkbench')}
        </button>
        
        <div className="header-content">
          <h1 className="page-title">
            <span className="title-icon">{toolIcon}</span>
            {toolName}
          </h1>
          <p className="page-description">
            {toolDescription}
          </p>
        </div>
      </div>

      <div className="ai-tool-template-main-content">
        <div className="content-grid">
          {/* 左侧功能面板 */}
          <div className="upload-section">
            {/* 动态渲染组件 - 根据组件类型调整样式 */}
            {sortedComponents.map(component => (
              component.type === COMPONENT_TYPES.PROMPT_INPUT ? (
                <div key={component.id} className="prompt-input-area">
                  <ComponentRenderer
                    component={component}
                    isGenerating={isGenerating}
                    value={componentValues[component.id]}
                    onChange={(value) => handleComponentValueChange(component.id, value)}
                    onValidationChange={(isValid, message) => handleComponentValidationChange(component.id, isValid, message)}
                  />
                </div>
              ) : (
                <ComponentRenderer
                  key={component.id}
                  component={component}
                  isGenerating={isGenerating}
                  value={componentValues[component.id]}
                  onChange={(value) => handleComponentValueChange(component.id, value)}
                  onValidationChange={(isValid, message) => handleComponentValidationChange(component.id, isValid, message)}
                />
              )
            ))}
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            {/* 生成按钮移至主内容区域，保持居中并粘性显示 */}
          </div>

          {/* 右侧结果区域 */}
          <ResultSection
            t={t}
            generatedImage={generatedImage}
            toolIcon={toolIcon}
            handleDownload={handleDownload}
            toolName={toolName}
            gradientColors={gradientColors}
          />
        </div>
        <div className="generate-sticky-bar center">
          <GenerateButtonWithCredits
            isGenerating={isGenerating}
            generationProgress={generationProgress}
            onClick={handleGenerate}
            disabled={!validateAllComponents().allValid || isGenerating}
            toolId={toolId}
            generationSettings={generationSettings}
          />
        </div>
      </div>

      {/* 登录弹窗 */}
      {showLoginModal && (
        <LoginModal 
          isOpen={showLoginModal}
          onClose={handleCloseLogin}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
    </div>
  );
};

export default AiToolTemplateV2;
